import {School, SchoolClass, Subscription} from "models"
import {useCallback, useEffect, useMemo} from "react"
import ModelEvents from "@kaspernj/api-maker/build/model-events"
import moment from "shared/moment"
import useCurrentUser from "@kaspernj/api-maker/build/use-current-user"
import useShape from "set-state-compare/src/use-shape"

const loadExpiredProTrial = async (s) => {
  const thirtyDaysAgo = moment().subtract(30, "days").toDate()
  const subscription = await Subscription
    .ransack({
      plan_identifier_eq: "pro_trial",
      contact_id_eq: s.m.currentUser.contactId(),
      without_active_period: true,
      last_period_ends_at_gteq: thirtyDaysAgo
    })
    .select({Subscription: ["createdAt"]})
    .first()

  s.set({expiredProTrial: subscription})
}

const loadSchoolsWithPro = async (s) => {
  const schoolsWithPro = await School
    .ransack({
      contact_children_relationships_child_id_eq: s.m.currentUser.contactId(),
      subscriptions_plan_identifier_eq: "pro",
      with_active_subscription: true
    })
    .toArray()

  s.set({schoolsWithPro})
}

const loadSchoolClassesWithPro = async (s) => {
  const schoolClassesWithPro = await SchoolClass
    .ransack({
      contact_children_relationships_child_id_eq: s.m.currentUser.contactId(),
      subscriptions_plan_identifier_eq: "pro",
      with_active_subscription: true
    })
    .toArray()

  s.set({schoolClassesWithPro})
}

const loadSchoolsThroughClassWithSchoolPro = async (s) => {
  const schoolsThroughClassWithSchoolPro = await School
    .ransack({
      school_classes_contact_children_relationships_child_id_eq: s.m.currentUser.contactId(),
      subscriptions_plan_identifier_eq: "pro",
      with_active_subscription: true
    })
    .toArray()

  s.set({schoolsThroughClassWithSchoolPro})
}

const loadUserProSubscriptions = async (s) => {
  const userProSubscriptionsQuery = await Subscription
    .ransack({
      plan_identifier_eq_any: ["pro", "pro_plus", "pro_trial"],
      contact_id_eq: s.m.currentUser.contactId(),
      with_active_period: true
    })
    .preload("plan")

  if (s.props?.userSubscriptions?.select) userProSubscriptionsQuery.select(s.props.userSubscriptions.select)
  if (s.props?.userSubscriptions?.preload) userProSubscriptionsQuery.preload(s.props.userSubscriptions.preload)

  const userProSubscriptions = await userProSubscriptionsQuery.toArray()
  const trialSubscription = userProSubscriptions.find((subscription) => subscription.plan()?.identifier() == "pro_trial")

  s.set({
    hasProTrial: Boolean(trialSubscription),
    trialSubscription,
    userProSubscriptions
  })
}

const useAnythingPro = (props) => {
  const s = useShape(props)
  const currentUser = useCurrentUser()

  s.meta.currentUser = currentUser
  s.useStates({
    anythingPro: null,
    expiredProTrial: null,
    hasProTrial: false,
    schoolClassesWithPro: null,
    schoolsWithPro: null,
    schoolsThroughClassWithSchoolPro: null,
    trialSubscription: null,
    userProSubscriptions: null
  })

  if (
    (
      s.s.schoolsWithPro?.length > 0 ||
      s.s.schoolClassesWithPro?.length > 0 ||
      s.s.userProSubscriptions?.length > 0 ||
      s.s.schoolsThroughClassWithSchoolPro?.length > 0
    ) &&
    s.s.anythingPro !== true
  ) {
    s.set({anythingPro: true})
  } else if (
    s.s.schoolsWithPro?.length === 0 &&
    s.s.schoolClassesWithPro?.length === 0 &&
    s.s.userProSubscriptions?.length === 0 &&
    s.s.schoolsThroughClassWithSchoolPro.length === 0 &&
    s.s.anythingPro !== false
  ) {
    s.set({anythingPro: false})
  }

  useMemo(() => {
    if (currentUser) {
      loadExpiredProTrial(s)
      loadSchoolsThroughClassWithSchoolPro(s)
      loadSchoolClassesWithPro(s)
      loadSchoolsWithPro(s)
      loadUserProSubscriptions(s)
    } else {
      s.set({
        expiredProTrial: null,
        hasProTrial: false,
        schoolClassesWithPro: null,
        schoolsThroughClassWithSchoolPro: null,
        schoolsWithPro: null,
        trialSubscription: null,
        userProSubscriptions: null
      })
    }
  }, [currentUser?.id()])

  const onStartProUserTrial = useCallback(() => {
    loadUserProSubscriptions(s)
  }, [])

  const onStartProUser = useCallback(() => {
    loadUserProSubscriptions(s)
  }, [])

  useEffect(() => {
    if (currentUser) {
      const webSocketSubscription = ModelEvents.connect(currentUser, "start_pro_user_trial", onStartProUserTrial)

      return () => {
        webSocketSubscription.unsubscribe()
      }
    }
  }, [currentUser?.id()])

  useEffect(() => {
    if (currentUser) {
      const webSocketSubscription = ModelEvents.connect(currentUser, "start_pro_user", onStartProUser)

      return () => {
        webSocketSubscription.unsubscribe()
      }
    }
  }, [currentUser?.id()])

  return s.state
}

export default useAnythingPro
